// KeyStorage — platform-abstracted persistence for the E2E encryption master key.
//
// - Web:      IndexedDB 'encryption_keys' store (weakest — browser sandbox only)
// - Electron: IPC → main process → safeStorage (OS keychain-backed)
// - iOS:      IndexedDB (Capacitor WebView sandbox; Keychain via plugin is future work)
//
// Keys are stored as raw Uint8Array (32 bytes). User ID namespacing ensures
// multiple accounts on the same device don't collide.

import { createSingleStore } from '../utils/idb-helpers.js';
import { PasskeyVault } from './passkey-vault.js';

const _KEY_STORE_NAME = 'encryption_keys';
const _KEY_DB_NAME = 'NotesAppKeysDB';
const _KEY_DB_VERSION = 1;

// `_plat()` is a host-injected global (core/platform.js, head-loaded); read it
// through an `any` view since the shape is platform-specific and dynamic.
function _plat(): any {
  return window.Platform;
}

const _store = createSingleStore({
  dbName: _KEY_DB_NAME,
  version: _KEY_DB_VERSION,
  store: _KEY_STORE_NAME,
  keyPath: 'id',
});
const _idbGet = (key: string) => _store.get(key);
const _idbPut = (record: any) => _store.put(record);
const _idbDelete = (key: string) => _store.delete(key);

// ── Device ID ────────────────────────────────────────────────────────────

function _generateDeviceId(): string {
  const arr = crypto.getRandomValues(new Uint8Array(16));
  return [...arr].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ── KeyStorage public interface ──────────────────────────────────────────

export const KeyStorage = {
  /**
   * Save the master key for the given user. `rawBytes` is the 32-byte raw AES
   * key; `userId` is the Supabase user ID (for namespacing).
   */
  async saveMasterKey(rawBytes: Uint8Array, userId: string): Promise<void> {
    const id = `mk_${userId}`;

    // Try the platform's secure store first (Electron → safeStorage).
    // Returns false on hosts without OS-level keychain (web, iOS today).
    if (await _plat()?.secureStore?.save(id, rawBytes)) return;

    // If the user has wrapped this key behind a passkey (web, opt-in),
    // skip the plain IDB write — the wrapped record in PasskeyVault is
    // the canonical store. saveMasterKey is idempotent in practice
    // (same rawBytes) so we don't need to re-wrap on every call.
    if (PasskeyVault && (await PasskeyVault.isEnrolled(id))) return;

    // Fallback: IndexedDB stores Uint8Array natively.
    await _idbPut({ id, key: rawBytes });
  },

  /**
   * Load the master key for the given user — 32-byte key, or null if not found.
   * Interactive: when a passkey is enrolled this delegates to PasskeyVault.unlock,
   * which surfaces a WebAuthn user-verification prompt and MAY throw
   * `PasskeyPrfMismatchError` if the passkey's PRF output can't unwrap the stored
   * key (that typed error is handled by boot-encryption.ts). Call only from a
   * user gesture; use `hasMasterKey` for a non-interactive existence probe.
   */
  async loadMasterKey(userId: string): Promise<Uint8Array | null> {
    const id = `mk_${userId}`;

    const fromPlatform = await _plat()?.secureStore?.load(id);
    if (fromPlatform) return fromPlatform;

    // Prefer the passkey-wrapped record on web when one is enrolled.
    // unlock() prompts the user — if they cancel we return null so the
    // app surfaces "encryption locked" instead of silently degrading
    // to plain IDB (which the enroll flow has already deleted).
    if (PasskeyVault && (await PasskeyVault.isEnrolled(id))) {
      return await PasskeyVault.unlock(id);
    }

    const rec = await _idbGet(id);
    if (!rec?.key) return null;

    // Upgrade path: a previous install kept the key in IDB on a platform
    // that has since gained a real OS keychain (e.g. iOS, where the
    // SecureStorage Capacitor plugin landed). On first load after the
    // upgrade, push the key into the keychain and drop the IDB row.
    // Best-effort — if the platform store rejects the write, the IDB
    // row stays put so the key is never lost.
    try {
      if (await _plat()?.secureStore?.save(id, rec.key)) {
        await _idbDelete(id);
      }
    } catch {
      /* leave the IDB row in place */
    }

    return rec.key;
  },

  /**
   * Check if a master key exists for the given user. Non-interactive: unlike
   * `loadMasterKey`, this never triggers a passkey `unlock()` (no WebAuthn
   * prompt, no `PasskeyPrfMismatchError` throw) — an enrolled passkey counts as
   * "present" without unwrapping the key. Safe to call outside a user gesture.
   */
  async hasMasterKey(userId: string): Promise<boolean> {
    const id = `mk_${userId}`;
    if (await _plat()?.secureStore?.load(id)) return true;
    if (PasskeyVault && (await PasskeyVault.isEnrolled(id))) return true;
    const rec = await _idbGet(id);
    return !!rec?.key;
  },

  /**
   * Delete the master key for the given user. On the Web/iOS path,
   * overwrite the bytes in place before removing the record so a stale
   * IDB page on disk is less likely to retain readable key material.
   * Best-effort — the storage engine may keep older copies regardless.
   */
  async deleteMasterKey(userId: string): Promise<void> {
    const id = `mk_${userId}`;

    if (await _plat()?.secureStore?.delete(id)) return;

    try {
      const rec = await _idbGet(id);
      if (rec?.key) {
        rec.key.fill(0);
        await _idbPut({ id, key: rec.key });
      }
    } catch {
      /* zeroing is best-effort; proceed to delete regardless */
    }
    await _idbDelete(id);
  },

  /**
   * Get or generate a persistent device ID.
   * Stored in localStorage (not sensitive — just an identifier).
   */
  getDeviceId(): string {
    let id = localStorage.getItem('encryption_device_id');
    if (!id) {
      id = _generateDeviceId();
      localStorage.setItem('encryption_device_id', id);
    }
    return id;
  },

  /** Get a human-readable device name. */
  getDeviceName(): string {
    const p = _plat();
    if (p?.isElectron) {
      if (p.os === 'darwin') return 'Desktop (Mac)';
      if (p.os === 'win32') return 'Desktop (Windows)';
      if (p.os === 'linux') return 'Desktop (Linux)';
      return 'Desktop';
    }
    if (p?.isCapacitor) return 'iOS';
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('OPR/') || ua.includes('Opera/')) return 'Opera';
    if (ua.includes('Chrome/') && ua.includes('Safari/')) return 'Chrome';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    return 'Web Browser';
  },
};
